import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { savedArtworksApi } from '../api/savedArtworksApi';
import Toast from '../components/Toast';

const SavedArtworksContext = createContext();

const getArtworkId = (artwork) => String(artwork?._id || artwork?.id || '');

const transformArtwork = (artwork) => {
  let image = null;
  if (artwork.imageData && artwork.imageMimeType) {
    image = `data:${artwork.imageMimeType};base64,${artwork.imageData}`;
  } else if (artwork.imageUrl) {
    image = artwork.imageUrl;
  }

  return {
    ...artwork,
    image,
    id: artwork._id,
  };
};

const readErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export function SavedArtworksProvider({ children }) {
  const [savedArtworks, setSavedArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const pendingIdsRef = useRef(new Set());
  const { user, isLoading: authLoading } = useAuthContext();

  const setPending = (artworkId, isPending) => {
    if (isPending) pendingIdsRef.current.add(artworkId);
    else pendingIdsRef.current.delete(artworkId);
    setPendingIds(new Set(pendingIdsRef.current));
  };

  const loadSavedArtworks = useCallback(async () => {
    if (!user) {
      setSavedArtworks([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await savedArtworksApi.getSavedArtworks();
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load saved artworks.');
      }
      setSavedArtworks((response.savedArtworks || []).map(transformArtwork));
    } catch (err) {
      console.error('Error loading saved artworks:', err);
      setError(readErrorMessage(err, 'Failed to load saved artworks.'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    loadSavedArtworks();
  }, [authLoading, loadSavedArtworks]);

  const isArtworkSaved = useCallback(
    (artworkId) => {
      const id = String(artworkId || '');
      if (!id) return false;
      return savedArtworks.some((artwork) => getArtworkId(artwork) === id);
    },
    [savedArtworks]
  );

  const isSaving = useCallback(
    (artworkId) => pendingIds.has(String(artworkId || '')),
    [pendingIds]
  );

  const toggleSaveArtwork = useCallback(
    async (artwork) => {
      if (!user) {
        setError('Please log in to save artworks.');
        return false;
      }

      const artworkId = getArtworkId(artwork);
      if (!artworkId || pendingIdsRef.current.has(artworkId)) return false;

      const wasSaved = savedArtworks.some((item) => getArtworkId(item) === artworkId);
      const snapshotItem = savedArtworks.find((item) => getArtworkId(item) === artworkId);

      setError(null);
      setPending(artworkId, true);
      setSavedArtworks((prev) => {
        if (wasSaved) {
          return prev.filter((item) => getArtworkId(item) !== artworkId);
        }
        const exists = prev.some((item) => getArtworkId(item) === artworkId);
        return exists ? prev : [...prev, { ...artwork, id: artworkId, _id: artworkId }];
      });

      try {
        const response = await savedArtworksApi.toggleSaveArtwork(artworkId);
        if (!response?.success) {
          throw new Error(response?.message || 'Could not update saved artworks.');
        }

        setSavedArtworks((prev) => {
          if (response.isSaved) {
            const exists = prev.some((item) => getArtworkId(item) === artworkId);
            return exists ? prev : [...prev, { ...artwork, id: artworkId, _id: artworkId }];
          }
          return prev.filter((item) => getArtworkId(item) !== artworkId);
        });
        return true;
      } catch (err) {
        console.error('Error toggling saved artwork:', err);
        setSavedArtworks((prev) => {
          if (wasSaved) {
            const exists = prev.some((item) => getArtworkId(item) === artworkId);
            return exists ? prev : [...prev, snapshotItem || { ...artwork, id: artworkId, _id: artworkId }];
          }
          return prev.filter((item) => getArtworkId(item) !== artworkId);
        });
        setError(readErrorMessage(err, 'Could not update saved artworks.'));
        return false;
      } finally {
        setPending(artworkId, false);
      }
    },
    [savedArtworks, user]
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({
      savedArtworks,
      toggleSaveArtwork,
      isArtworkSaved,
      isSaving,
      loading,
      error,
      clearError,
      loadSavedArtworks,
    }),
    [
      savedArtworks,
      toggleSaveArtwork,
      isArtworkSaved,
      isSaving,
      loading,
      error,
      clearError,
      loadSavedArtworks,
    ]
  );

  return (
    <SavedArtworksContext.Provider value={value}>
      {children}
      <Toast
        message={error}
        type="error"
        isVisible={Boolean(error)}
        onClose={clearError}
      />
    </SavedArtworksContext.Provider>
  );
}

export const useSavedArtworks = () => useContext(SavedArtworksContext);
